import { useEffect, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { api, API } from "@/lib/api";
import { featureInfo } from "@/lib/featureInfo";

let loaderPromise = null;
const TYPE_COLORS = { Agricultural: "#10B981", Irrigated: "#34D399", Residential: "#F59E0B", Commercial: "#C85A32", Forest: "#047857", Barren: "#94A3B8" };
function loadGoogle(key) {
  if (window.google?.maps) return Promise.resolve();
  if (!loaderPromise) {
    loaderPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = `https://maps.googleapis.com/maps/api/js?key=${key}&v=weekly`;
      s.async = true;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  return loaderPromise;
}

export function GoogleMapView({ apiKey, data, onSelect, onFail, extraLayers = [], flyTo = null, onView, onCursor, myLocation = null, highlight = null }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const locRef = useRef([]);
  const hlRef = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !window.google) return;
    if (hlRef.current) { hlRef.current.setMap(null); hlRef.current = null; }
    if (highlight) {
      const layer = new window.google.maps.Data({ map, style: { strokeColor: "#FDE047", strokeWeight: 5, fillColor: "#FDE047", fillOpacity: 0.2, zIndex: 999, clickable: false } });
      layer.addGeoJson({ type: "Feature", properties: {}, geometry: highlight });
      hlRef.current = layer;
    }
  }, [highlight, data, extraLayers]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !window.google) return;
    locRef.current.forEach((o) => o.setMap(null));
    locRef.current = [];
    if (myLocation) {
      const pos = { lat: myLocation.lat, lng: myLocation.lng };
      locRef.current.push(new window.google.maps.Circle({ map, center: pos, radius: myLocation.accuracy, strokeColor: "#2563EB", strokeWeight: 1, fillColor: "#3B82F6", fillOpacity: 0.15 }));
      locRef.current.push(new window.google.maps.Marker({ map, position: pos, title: `You are here (±${Math.round(myLocation.accuracy)} m)`, icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 8, fillColor: "#2563EB", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 3 } }));
    }
  }, [myLocation, data, extraLayers]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !flyTo) return;
    if (flyTo.zoomDelta) { map.setZoom(map.getZoom() + flyTo.zoomDelta); return; }
    if (flyTo.bounds) {
      const [[s, w], [n, e]] = flyTo.bounds;
      map.fitBounds(new window.google.maps.LatLngBounds({ lat: s, lng: w }, { lat: n, lng: e }), 40);
    } else if (flyTo.center) {
      map.panTo({ lat: flyTo.center[0], lng: flyTo.center[1] });
      map.setZoom(flyTo.zoom ?? 14);
    }
  }, [flyTo]);

  useEffect(() => {
    // Google reports key problems (expired / API not enabled) via this global callback
    window.gm_authFailure = () => { setFailed(true); onFail?.(); };
    return () => { delete window.gm_authFailure; };
  }, [onFail]);

  useEffect(() => {
    if (!apiKey) return;
    loadGoogle(apiKey).then(() => {
      if (!ref.current) return;
      const map = new window.google.maps.Map(ref.current, { center: { lat: 21.27, lng: 81.87 }, zoom: 7, mapTypeId: "hybrid", mapTypeControl: true, streetViewControl: false, fullscreenControl: false });
      mapRef.current = map;
      map.addListener("idle", () => { const c = map.getCenter(); onView?.({ center: [c.lat(), c.lng()], zoom: map.getZoom() }); });
      map.addListener("mousemove", (e) => onCursor?.([e.latLng.lat(), e.latLng.lng()]));
      map.addListener("mouseout", () => onCursor?.(null));
      const info = new window.google.maps.InfoWindow();
      const bounds = new window.google.maps.LatLngBounds();
      data?.features?.forEach((f) => {
        const path = f.geometry.coordinates[0].map(([lng, lat]) => ({ lat, lng }));
        path.forEach((p) => bounds.extend(p));
        const color = TYPE_COLORS[f.properties.land_type] || "#10B981";
        const poly = new window.google.maps.Polygon({ paths: path, strokeColor: color, strokeWeight: 2, fillColor: color, fillOpacity: 0.35, map });
        poly.addListener("mouseover", () => poly.setOptions({ fillOpacity: 0.6, strokeWeight: 3 }));
        poly.addListener("mouseout", () => poly.setOptions({ fillOpacity: 0.35, strokeWeight: 2 }));
        poly.addListener("click", (e) => {
          info.setContent(`<b>Khasra ${f.properties.khasra}</b><br/>${f.properties.village}, ${f.properties.district}`);
          info.setPosition(e.latLng);
          info.open(map);
          onSelect?.({ ...f.properties, geometry: f.geometry });
        });
      });
      if (data?.features?.length) map.fitBounds(bounds, 60);
      const token = localStorage.getItem("cgland_token");
      extraLayers.forEach((l, idx) => {
        if (l.kind === "raster") {
          const [[s, w], [n, e]] = l.bounds;
          new window.google.maps.GroundOverlay(`${API}/map/layers/${l.id}/overlay.png?auth=${token}`, { north: n, south: s, east: e, west: w }, { map, opacity: l.opacity ?? 0.5 });
        } else {
          (l.geojson ? Promise.resolve({ data: l.geojson }) : api.get(`/map/layers/${l.id}/geojson`)).then(({ data: gj }) => {
            const layer = new window.google.maps.Data({ map });
            const f = l.filter;
            const filtered = f?.field && f.value != null ? { ...gj, features: gj.features.filter((ft) => String(ft.properties?.[f.field] ?? "") === f.value) } : gj;
            layer.addGeoJson(filtered);
            layer.addListener("click", (ev) => {
              const props = {}; ev.feature.forEachProperty((v, k) => { props[k] = v; });
              const gj = filtered.features.find((ft) => JSON.stringify(ft.properties) === JSON.stringify(props)) || { properties: props, geometry: null };
              onSelect?.(featureInfo(l, gj, ev.latLng ? { lat: ev.latLng.lat(), lng: ev.latLng.lng() } : null));
            });
            layer.setStyle((f) => {
              const base = { strokeColor: l.color, fillColor: l.color, fillOpacity: l.opacity ?? 0.45, strokeWeight: 2, zIndex: 10 + idx };
              if (f.getGeometry()?.getType() === "Point") base.icon = { path: window.google.maps.SymbolPath.CIRCLE, scale: 6, fillColor: l.color, fillOpacity: 0.9, strokeColor: "#fff", strokeWeight: 1.5 };
              return base;
            });
          }).catch(() => {});
        }
      });
    }).catch(() => setFailed(true));
  }, [apiKey, data, onSelect, extraLayers]); // eslint-disable-line react-hooks/exhaustive-deps

  if (failed) {
    return (
      <div data-testid="google-map-load-error" className="h-full w-full grid place-items-center bg-slate-900 text-white p-8 text-center">
        <div className="max-w-sm"><AlertTriangle className="mx-auto text-amber-400" /><p className="mt-4 font-semibold">Google Maps failed to load</p><p className="text-sm text-slate-400 mt-1">Check that the API key has Maps JavaScript API enabled and this domain is allowed. Switch to Leaflet meanwhile.</p></div>
      </div>
    );
  }

  if (!apiKey) {
    return (
      <div data-testid="google-map-unavailable" className="h-full w-full grid place-items-center bg-slate-900 text-white p-8 text-center">
        <div className="max-w-sm"><AlertTriangle className="mx-auto text-amber-400" /><p className="mt-4 font-semibold">Google Maps key not configured</p><p className="text-sm text-slate-400 mt-1">Add GOOGLE_MAPS_API_KEY to the backend .env to enable this provider. Leaflet remains available.</p></div>
      </div>
    );
  }
  return <div ref={ref} data-testid="google-map" className="h-full w-full" />;
}
